import { format } from "date-fns";
import Location from "../../assets/icons/Location";
import Time from "../../assets/icons/Time";
import Warning from "../../assets/icons/Warning";
import CarouselLayout from "../../containers/Layouts/CarouselLayout/CarouselLayout";
import ArtworkPending from "../LiveAuction/ArtworkPending";
import PageNotFound from "../Errors/PageNotFound";
import Error400 from "../Errors/Error400";
import Error500 from "../Errors/Error500";
import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "react-bootstrap";
import ResultLelang from "../../components/Modals/ResultLelang";
import { zeroPad } from "react-countdown";
import * as io from "socket.io-client";
import { useParams } from "react-router-dom";
import logo from "../../assets/images/caready-logo.webp";
import toast from "react-hot-toast";
import { textDark, danger } from "../../config/colors";
import NoImage from "../../assets/images/NoImage.jpg";

const defaultImage = NoImage;

const initialDataModal = {
  lot_number: "",
  data_unit: {},
};

const initialBid = {
  offer_price: 0,
  type: "-",
};

const LiveAuctionPublic = () => {
  const { id } = useParams();
  const [isOpen, setIsOpen] = useState(false);
  const [result, setResult] = useState("LOADING");
  const [liveAuction, setLiveAuction] = useState("STARTED");
  const [dataModal, setDataModal] = useState({ ...initialDataModal });
  const [isSelectingWinner, setIsSelectingWinner] = useState(false);
  const [joinError, setJoinError] = useState(false); // Track join error state
  const [errorStatusCode, setErrorStatusCode] = useState(null); // Track error status code (400, 500, etc.)
  const [errorMessage, setErrorMessage] = useState(""); // Track error message from response

  // list
  const [data, setData] = useState({});
  const [auction, setAuction] = useState({});
  const [images, setImages] = useState([]);
  const [biddingList, setBiddingList] = useState([]);

  // bid
  const [highestBid, setHighestBid] = useState({ ...initialBid });
  const [counter, setCounter] = useState(null);
  const [totalNipl, setTotalNipl] = useState(0);

  // Socket connection tracking
  const joinTimeoutRef = useRef(null);
  const joinAttemptsRef = useRef(0);
  const isJoiningRef = useRef(false);
  const MAX_JOIN_ATTEMPTS = 5;
  const JOIN_RETRY_DELAY = 3000; // 3 seconds

  // API fetch tracking
  const isDataFetchedRef = useRef(false);

  // Socket status tracking - using refs to prevent race conditions
  const [socketStatus, setSocketStatus] = useState("connecting");
  const socketStatusRef = useRef("connecting"); // Track current status in ref
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [maxReconnectReached, setMaxReconnectReached] = useState(false);
  const reconnectCountRef = useRef(0);
  const stableConnectionTimerRef = useRef(null);
  const isResettingRef = useRef(false); // Prevent concurrent resets

  // Socket setup
  const URL = process.env.REACT_APP_SOCKET;
  const MAX_RECONNECT_ATTEMPTS = 10;
  const STABLE_CONNECTION_DELAY = 5000; // 5 seconds

  const socketRef = useRef(null);
  const auctionSocketRoomRef = useRef(null);
  const nextLotModalTimeoutRef = useRef(null); // Track modal auto-close timeout

  // Initialize socket only once
  if (!socketRef.current) {
    socketRef.current = io(URL, {
      query: { token: id },
      transports: ["websocket"],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
      reconnectionDelay: 2000,
      timeout: 10000,
      forceNew: false,
    });
  }

  // Fetch initial auction data
  const getDetailAuction = useCallback(async () => {
    try {
      console.log("🔍 Fetching initial auction data...");
      const response = await fetch(`${process.env.REACT_APP_URL}api/v2/public/detail-auction/${id}`);

      if (response.ok) {
        const result = await response.json();

        if (result?.status && result?.data) {
          const auctionData = result.data;

          // Set auction basic info
          setAuction({
            ...auctionData,
            nipls_count: 0, // Will be updated by socket
          });

          setLiveAuction(auctionData?.status || "COMING");

          // Set socket room using auction ID from API response
          const socketRoom = `auction:${auctionData.id}`;
          auctionSocketRoomRef.current = socketRoom;
          console.log(`✅ Socket room set to: ${socketRoom}`);

          // Mark data as fetched
          isDataFetchedRef.current = true;

          console.log("✅ Initial auction data loaded:", auctionData);

          // Now safe to join socket if connected
          if (socketRef.current && socketRef.current.connected) {
            console.log("✅ Data fetched and socket connected, now joining socket room...");
            // Trigger join by calling handleJoinAuction directly
            // We'll define it below, so we call it via setTimeout to ensure it's defined
            setTimeout(() => {
              if (
                socketRef.current &&
                socketRef.current.connected &&
                isDataFetchedRef.current &&
                auctionSocketRoomRef.current
              ) {
                console.log(`🔔 Emitting JoinAuction to room: ${auctionSocketRoomRef.current}`);
                socketRef.current.emit("JoinAuctionPublic");
              }
            }, 100);
          }
        } else {
          console.warn("⚠️ Invalid auction data structure");
          setLiveAuction("NOT_FOUND");
        }
      } else {
        const errorBody = await response.json().catch(() => ({})); // Attempt to parse JSON error body
        const statusCode = response.status;
        const errorMsg =
          errorBody?.errors?.details || errorBody?.message || `Failed to fetch auction data: ${statusCode}`;

        console.error("❌ Failed to fetch auction data:", errorMsg, "Status code:", statusCode);
        setLiveAuction("JOIN_ERROR"); // Use JOIN_ERROR to trigger error page logic
        setErrorStatusCode(statusCode);
        setErrorMessage(errorMsg);
      }
    } catch (error) {
      console.error("❌ Error fetching auction data:", error);
      setLiveAuction("JOIN_ERROR");
      setErrorStatusCode(500);
      setErrorMessage(error?.message || "An unexpected error occurred");
    }
  }, [id]);

  const handleJoinAuction = useCallback(() => {
    // Check if data has been fetched first
    if (!isDataFetchedRef.current) {
      console.warn("⚠️ Cannot join auction: API data not fetched yet");
      return;
    }

    // Check if socket room is set
    if (!auctionSocketRoomRef.current) {
      console.warn("⚠️ Cannot join auction: socket room not set yet");
      return;
    }

    if (!socketRef.current) {
      console.warn("⚠️ Cannot join auction: socket is null");
      return;
    }

    if (!socketRef.current.connected) {
      console.warn("⚠️ Cannot join auction: socket not connected");
      return;
    }

    if (isJoiningRef.current) {
      console.warn("⚠️ Already attempting to join auction");
      return;
    }

    // Clear any existing timeout before starting new attempt
    if (joinTimeoutRef.current) {
      clearTimeout(joinTimeoutRef.current);
      joinTimeoutRef.current = null;
    }

    isJoiningRef.current = true;
    const currentAttempt = joinAttemptsRef.current + 1;
    console.log(
      `🔔 Emitting JoinAuction to room: ${auctionSocketRoomRef.current} (attempt ${currentAttempt}/${MAX_JOIN_ATTEMPTS})`
    );

    socketRef.current.emit("JoinAuctionPublic");

    // Set timeout to retry if no response
    joinTimeoutRef.current = setTimeout(() => {
      // Only proceed if still in joining state
      if (!isJoiningRef.current) {
        return;
      }

      isJoiningRef.current = false;
      joinAttemptsRef.current += 1;

      if (joinAttemptsRef.current < MAX_JOIN_ATTEMPTS) {
        console.log(`⚠️ No response from JoinAuction, retrying... (${joinAttemptsRef.current}/${MAX_JOIN_ATTEMPTS})`);

        // Retry join
        handleJoinAuction();
      } else {
        console.error(`❌ Failed to join auction after ${MAX_JOIN_ATTEMPTS} attempts`);

        joinAttemptsRef.current = 0;
      }
    }, JOIN_RETRY_DELAY);
  }, [MAX_JOIN_ATTEMPTS, JOIN_RETRY_DELAY]);

  const secondsToTime = (secs) => {
    let divisor_for_minutes = secs % (60 * 60);
    let minutes = Math.floor(divisor_for_minutes / 60);

    let divisor_for_seconds = divisor_for_minutes % 60;
    let seconds = Math.ceil(divisor_for_seconds);

    return zeroPad(minutes) + ":" + zeroPad(seconds);
  };

  useEffect(() => {
    const timer = counter > 0 && setInterval(() => setCounter(counter - 1), 1000);

    // When local counter reaches 0, show selecting winner modal immediately
    // This provides instant feedback while waiting for socket confirmation
    if (counter === 0 && liveAuction === "STARTED" && !isSelectingWinner) {
      setIsSelectingWinner(true);
      setDataModal((prev) => ({
        ...prev,
        lot_number: auction?.auction_lane_name || "",
      }));
    }

    return () => clearInterval(timer);
  }, [counter, liveAuction, isSelectingWinner, auction?.auction_lane_name]);

  // Reset selecting winner state when unit changes (new lot)
  useEffect(() => {
    if (data?.id) {
      // When unit ID changes, reset selecting winner state
      setIsSelectingWinner(false);
    }
  }, [data?.id]);

  // Socket event handlers
  const handleSocketAuction = useCallback((auctionData, skipTotalNiplUpdate = false) => {
    try {
      const currentAuction = auctionData?.auction || {};

      if (auctionData?.auction) {
        if (currentAuction?.status === "CLOSED") {
          setIsOpen(false);
        }

        setAuction({
          ...currentAuction,
          nipls_count: auctionData?.auction_nipls?.length || 0,
        });
        setLiveAuction(currentAuction?.status);

        // Check if auction is STARTED but seconds_left_before is 0 (selecting winner state)
        if (
          currentAuction?.status === "STARTED" &&
          (auctionData?.seconds_left_before === 0 || auctionData?.seconds_left_before === null)
        ) {
          setIsSelectingWinner(true);
          // Set modal data when entering selecting winner state
          setDataModal((prev) => ({
            ...prev,
            lot_number: currentAuction?.auction_lane_name || "",
          }));
        } else {
          setIsSelectingWinner(false);
        }
      }

      if (auctionData?.current_unit !== null && auctionData?.current_unit?.length > 0) {
        let unitNow = auctionData?.current_unit?.[0] || {};

        const normalizedUnit = {
          ...unitNow,
          stock_informations: unitNow?.stock_informations || unitNow?.stock_only_value_informations || [],
          stock_documents: unitNow?.stock_documents || unitNow?.stock_document_only_value || [],
        };

        setData(normalizedUnit);
        setImages(normalizedUnit?.pictures || []);

        // Process bidder data
        const bidders = unitNow?.bidders || [];
        console.log("📊 Processing bidders:", bidders);
        
        if (bidders.length > 0) {
          const processedBidders = bidders
            .slice()
            .reverse()
            .map((bidder) => {
              // Determine the type
              let type = "Online Bidder";
              if (bidder.email === "Proxy bid" || bidder.email === "Proxy bidder") {
                type = "Proxy bid";
              } else if (bidder.email === "Offline Bidder") {
                type = "Offline Bidder";
              }

              return {
                ...bidder,
                type,
                offer_price: parseInt(bidder.offer) || 0,
              };
            });

          console.log("📊 Processed bidders:", processedBidders);

          const highestBidData = {
            email: processedBidders[0]?.email || "",
            offer_price: parseInt(processedBidders[0]?.offer_price) || 0,
            type: processedBidders[0]?.type || "Harga Dasar",
          };

          const biddingListData = processedBidders.filter((_, idx) => idx !== 0);

          console.log("📊 Highest bid:", highestBidData);
          console.log("📊 Bidding list:", biddingListData);

          setHighestBid(highestBidData);
          setBiddingList(biddingListData);
        } else {
          console.log("📊 No bidders, setting base price");
          setHighestBid({
            email: "base_price",
            type: "Harga Dasar",
            offer_price: unitNow?.base_price || 0,
          });
          setBiddingList([]);
        }
      }

      setCounter(auctionData?.seconds_left_before || null);

      // Only update total_nipl if not skipped
      if (!skipTotalNiplUpdate) {
        setTotalNipl(auctionData?.total_nipl || 0);
      }
    } catch (error) {
      console.error("Error in handleSocketAuction:", error);
    }
  }, []);

  const handleSocketError = useCallback((err) => {
    console.log("❌ Socket error:", err);

    // Parse error message
    let errorMessage = "Terjadi kesalahan pada koneksi";
    let statusCode = null;

    // Handle different error formats
    if (err?.status_code) {
      statusCode = err.status_code;
    }

    if (err?.errors?.details) {
      errorMessage = err.errors.details;
    } else if (err?.message) {
      errorMessage = err.message;
    } else if (typeof err === "string") {
      errorMessage = err;
    } else if (err?.error) {
      errorMessage = err.error;
    }

    console.error("Socket error:", errorMessage, "Status code:", statusCode);

    // Set error state to show error page for any socket error
    setJoinError(true);
    setLiveAuction("JOIN_ERROR");
    setErrorStatusCode(statusCode); // Store status code for rendering appropriate error page
    setErrorMessage(errorMessage); // Store error message to display on error page

    // Show appropriate error message
    if (statusCode === 404) {
      // Not found error
      toast.error(
        <div>
          <strong>Lelang Tidak Ditemukan</strong>
          <p style={{ margin: "5px 0 0 0", fontSize: "14px" }}>
            Lelang yang Anda cari tidak ditemukan atau sudah tidak tersedia.
          </p>
        </div>,
        {
          duration: 5000,
          position: "top-center",
        }
      );
    } else {
      // Generic error - show error page
      toast.error(
        <div>
          <strong>Terjadi Kesalahan</strong>
          <p style={{ margin: "5px 0 0 0", fontSize: "14px" }}>{errorMessage}</p>
        </div>,
        {
          duration: 4000,
          position: "top-center",
        }
      );
    }
  }, []);

  const handleSocketEvent = useCallback(
    (eventType, response) => {
      console.log(`📩 ${eventType}:`, response);

      // Check for error in response or non-200 status code
      if (response?.error || response?.errors || (response?.status_code && response?.status_code !== 200)) {
        const errorMsg = response?.errors?.details || response?.error || response?.message || "Terjadi kesalahan";
        const statusCode = response?.status_code;
        console.error(`❌ ${eventType} error:`, errorMsg, "Status code:", statusCode);

        // If JoinAuctionResponse has error, show error page
        if (eventType === "JoinAuctionResponse") {
          isJoiningRef.current = false;
          // Clear timeout to prevent duplicate retry
          if (joinTimeoutRef.current) {
            clearTimeout(joinTimeoutRef.current);
            joinTimeoutRef.current = null;
          }

          // Set join error state and show error page (for ANY error, not just non-200 status)
          console.error(`❌ JoinAuction failed, showing error page...`);
          setJoinError(true); // Set error state to show error page
          setLiveAuction("JOIN_ERROR"); // Set special status for join error
          setErrorStatusCode(statusCode); // Store status code for rendering appropriate error page
          setErrorMessage(errorMsg); // Store error message to display on error page

          toast.error(
            <div>
              <strong>Gagal Bergabung ke Lelang</strong>
              <p style={{ margin: "5px 0 0 0", fontSize: "14px" }}>
                {errorMsg || "Tidak dapat bergabung ke lelang ini."}
              </p>
            </div>,
            {
              duration: 3000,
              position: "top-center",
            }
          );
        }

        return;
      }

      // Validate response data (skip validation for TotalNiplResponse as it has different structure)
      if (eventType !== "TotalNiplResponse" && !response?.data) {
        console.warn(`⚠️ ${eventType}: Invalid response data`);
        return;
      }

      // Clear join timeout and reset attempts on successful response
      if (eventType === "JoinAuctionResponse") {
        if (joinTimeoutRef.current) {
          clearTimeout(joinTimeoutRef.current);
          joinTimeoutRef.current = null;
        }
        isJoiningRef.current = false;
        joinAttemptsRef.current = 0;
        console.log("✅ Successfully joined auction room");
      }

      // Update auction state first (skip for TotalNiplResponse as it doesn't have auction data)
      // Skip totalNipl update for events that don't include total_nipl data
      if (eventType !== "TotalNiplResponse") {
        const skipTotalNiplUpdate = ["SendBidResponse", "FloorResponse", "ReSellResponse"].includes(eventType);
        handleSocketAuction(response.data, skipTotalNiplUpdate);
      }

      switch (eventType) {
        case "TotalNiplResponse": {
          // Handle NIPL response data
          console.log("📊 Full TotalNiplResponse:", response);
          let niplData = response?.data?.data || response?.data;
          const remaining = niplData?.total_nipl;

          if (niplData && niplData.total_nipl !== undefined) {
            setTotalNipl(remaining);
            console.log("📊 Updated total NIPL:", remaining);
          } else {
            console.warn("⚠️ TotalNiplResponse: Could not find NIPL data", response);
          }
          break;
        }

        case "NextLotResponse": {
          const extra = response?.extra || null;

          // Reset selecting winner state when moving to next lot
          setIsSelectingWinner(false);

          // Set modal data if unit exists
          if (extra?.unit) {
            setDataModal((prev) => ({
              ...prev,
              data_unit: extra.unit,
            }));
          }

          // Always show modal for both TERJUAL and TIDAK TERJUAL
          setResult(extra?.buyer ? "TERJUAL" : "TIDAK TERJUAL");
          setIsOpen(true);

          // Clear any existing timeout to prevent multiple timers
          if (nextLotModalTimeoutRef.current) {
            clearTimeout(nextLotModalTimeoutRef.current);
            nextLotModalTimeoutRef.current = null;
          }

          // Auto close modal after 3 seconds
          nextLotModalTimeoutRef.current = setTimeout(() => {
            setIsOpen(false);
            nextLotModalTimeoutRef.current = null;
          }, 3000);

          break;
        }

        case "JoinAuctionResponse":
        case "PendingResponse":
        case "StartAuctionResponse":
        case "FloorResponse":
        case "ReSellResponse":
        case "SendBidResponse":
        default:
          // Already handled by handleSocketAuction above
          break;
      }
    },
    [handleSocketAuction]
  );

  // Helper function to update socket status
  const updateSocketStatus = useCallback((status) => {
    socketStatusRef.current = status;
    setSocketStatus(status);
  }, []);

  // Setup socket listeners
  useEffect(() => {
    console.log("🟡 Setting up socket listeners...");

    if (!socketRef.current) {
      console.warn("⚠️ Socket ref is null, skipping listener setup");
      return;
    }

    // Helper to safely clear stable connection timer
    const clearStableTimer = () => {
      if (stableConnectionTimerRef.current) {
        clearTimeout(stableConnectionTimerRef.current);
        stableConnectionTimerRef.current = null;
      }
    };

    // Helper to safely reset reconnect counter
    const resetReconnectCounter = () => {
      if (isResettingRef.current) {
        console.log("⚠️ Already resetting reconnect counter, skipping");
        return;
      }

      isResettingRef.current = true;
      clearStableTimer();

      stableConnectionTimerRef.current = setTimeout(() => {
        console.log("🟢 Connection stable, resetting reconnect counter");
        reconnectCountRef.current = 0;
        setReconnectAttempts(0);
        setMaxReconnectReached(false);
        isResettingRef.current = false;
      }, STABLE_CONNECTION_DELAY);
    };

    const handleConnect = () => {
      console.log("✅ Connected to server");
      updateSocketStatus("connected");

      // Only join if data has been fetched
      if (isDataFetchedRef.current) {
        console.log("✅ Data already fetched, joining socket room...");
        handleJoinAuction();
      } else {
        console.log("⏳ Waiting for API data before joining socket room...");
      }

      resetReconnectCounter();
    };

    const handleDisconnect = (reason) => {
      console.log("🔌 Disconnected from server:", reason);
      updateSocketStatus("disconnected");
      clearStableTimer();
      isResettingRef.current = false;
    };

    const handleReconnect = () => {
      console.log("🔄 Reconnected to server");
      updateSocketStatus("connected");

      // Only join if data has been fetched
      if (isDataFetchedRef.current) {
        console.log("✅ Data already fetched, rejoining socket room...");
        handleJoinAuction();
      } else {
        console.log("⏳ Waiting for API data before joining socket room...");
      }

      resetReconnectCounter();
    };

    const handleReconnectAttempt = () => {
      // Increment cumulative counter
      reconnectCountRef.current += 1;
      const totalAttempts = reconnectCountRef.current;

      console.log(`🔄 Reconnection attempt ${totalAttempts} of ${MAX_RECONNECT_ATTEMPTS}`);
      setReconnectAttempts(totalAttempts);
      updateSocketStatus("reconnecting");

      if (totalAttempts >= MAX_RECONNECT_ATTEMPTS) {
        console.log("💥 Maximum reconnection attempts reached");
        setMaxReconnectReached(true);
        updateSocketStatus("failed");
        // Disconnect socket to stop further attempts
        if (socketRef.current) {
          socketRef.current.disconnect();
        }
      }
    };

    const handleReconnectError = (error) => {
      console.log("❌ Reconnection error:", error);
      // Only update status if not already failed
      if (socketStatusRef.current !== "failed") {
        updateSocketStatus("reconnecting");
      }
    };

    const handleReconnectFailed = () => {
      console.log("💥 Reconnection failed - maximum attempts reached");
      updateSocketStatus("failed");
      setMaxReconnectReached(true);
    };

    const handleConnectError = (err) => {
      console.log("❌ Connection error:", err?.message || err);
    };

    const handleJoinAuctionResponse = (response) => handleSocketEvent("JoinAuctionResponse", response);
    const handleFloorResponse = (response) => handleSocketEvent("FloorResponse", response);
    const handleStartAuctionResponse = (response) => handleSocketEvent("StartAuctionResponse", response);
    const handleTotalNiplResponse = (response) => handleSocketEvent("TotalNiplResponse", response);
    const handlePendingResponse = (response) => handleSocketEvent("PendingResponse", response);
    const handleReSellResponse = (response) => handleSocketEvent("ReSellResponse", response);
    const handleSendBidResponse = (response) => handleSocketEvent("SendBidResponse", response);
    const handleNextLotResponse = (response) => handleSocketEvent("NextLotResponse", response);

    socketRef.current.on("connect", handleConnect);
    socketRef.current.on("disconnect", handleDisconnect);
    socketRef.current.on("reconnect", handleReconnect);
    socketRef.current.on("reconnect_attempt", handleReconnectAttempt);
    socketRef.current.on("reconnect_error", handleReconnectError);
    socketRef.current.on("reconnect_failed", handleReconnectFailed);
    socketRef.current.on("connect_error", handleConnectError);
    socketRef.current.on("JoinAuctionResponse", handleJoinAuctionResponse);
    socketRef.current.on("FloorResponse", handleFloorResponse);
    socketRef.current.on("StartAuctionResponse", handleStartAuctionResponse);
    socketRef.current.on("TotalNiplResponse", handleTotalNiplResponse);
    socketRef.current.on("PendingResponse", handlePendingResponse);
    socketRef.current.on("ReSellResponse", handleReSellResponse);
    socketRef.current.on("SendBidResponse", handleSendBidResponse);
    socketRef.current.on("NextLotResponse", handleNextLotResponse);
    socketRef.current.on("error", handleSocketError);

    return () => {
      console.log("🛑 Cleaning up socket listeners...");
      clearStableTimer();
      isResettingRef.current = false;

      if (socketRef.current) {
        socketRef.current.off("connect", handleConnect);
        socketRef.current.off("disconnect", handleDisconnect);
        socketRef.current.off("reconnect", handleReconnect);
        socketRef.current.off("reconnect_attempt", handleReconnectAttempt);
        socketRef.current.off("reconnect_error", handleReconnectError);
        socketRef.current.off("reconnect_failed", handleReconnectFailed);
        socketRef.current.off("connect_error", handleConnectError);
        socketRef.current.off("JoinAuctionResponse", handleJoinAuctionResponse);
        socketRef.current.off("FloorResponse", handleFloorResponse);
        socketRef.current.off("StartAuctionResponse", handleStartAuctionResponse);
        socketRef.current.off("TotalNiplResponse", handleTotalNiplResponse);
        socketRef.current.off("PendingResponse", handlePendingResponse);
        socketRef.current.off("ReSellResponse", handleReSellResponse);
        socketRef.current.off("SendBidResponse", handleSendBidResponse);
        socketRef.current.off("NextLotResponse", handleNextLotResponse);
        socketRef.current.off("error", handleSocketError);
      }
    };
  }, [handleJoinAuction, handleSocketEvent, handleSocketError, updateSocketStatus]);

  // Fetch initial auction data on mount
  useEffect(() => {
    getDetailAuction();
  }, [getDetailAuction]);

  // Handle room changes when id changes
  useEffect(() => {
    if (socketRef.current && socketRef.current.connected) {
      console.log("🔄 Room changed, rejoining auction...");
      handleJoinAuction();
    }
  }, [id, handleJoinAuction]);

  // Cleanup socket on component unmount
  useEffect(() => {
    return () => {
      console.log("🛑 Component unmounting, disconnecting socket...");

      // Clear join timeout
      if (joinTimeoutRef.current) {
        clearTimeout(joinTimeoutRef.current);
        joinTimeoutRef.current = null;
      }

      // Clear stable connection timer
      if (stableConnectionTimerRef.current) {
        clearTimeout(stableConnectionTimerRef.current);
        stableConnectionTimerRef.current = null;
      }

      // Clear next lot modal timeout
      if (nextLotModalTimeoutRef.current) {
        clearTimeout(nextLotModalTimeoutRef.current);
        nextLotModalTimeoutRef.current = null;
      }

      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  // Handle visibility change and focus events
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && socketRef.current && socketRef.current.connected) {
        console.log("👁️ Page became visible, rejoining auction...");
        handleJoinAuction();
      }
    };

    const handleFocus = () => {
      if (socketRef.current && socketRef.current.connected) {
        console.log("🎯 Window focused, rejoining auction...");
        handleJoinAuction();
      }
    };

    const handleOnline = () => {
      console.log("🌐 Network back online, reconnecting...");
      if (socketRef.current && !socketRef.current.connected) {
        socketRef.current.connect();
      }
    };

    const handleOffline = () => {
      console.log("📴 Network offline");
    };

    // Add event listeners
    window.addEventListener("focus", handleFocus);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [handleJoinAuction]);

  const handleRefreshPage = () => {
    window.location.reload();
  };

  // Loading placeholder component
  const LoadingPlaceholder = () => {
    const isFailed = socketStatus === "failed" || maxReconnectReached;

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(255, 255, 255, 0.98)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9998,
        }}
      >
        <div
          style={{
            textAlign: "center",
            maxWidth: "400px",
            padding: "40px",
          }}
        >
          {/* Animated Logo or Error Icon */}
          <div
            style={{
              marginBottom: "30px",
              animation: isFailed ? "none" : "pulse 2s ease-in-out infinite",
            }}
          >
            {isFailed ? (
              <div style={{ marginBottom: "10px" }}>
                <Warning color={danger} width="64" height="64" />
              </div>
            ) : (
              <img
                src={logo}
                alt="Caready Logo"
                style={{
                  height: "80px",
                  width: "auto",
                }}
              />
            )}
          </div>

          {/* Loading Spinner - Only show when not failed */}
          {!isFailed && (
            <div
              style={{
                marginBottom: "20px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  width: "50px",
                  height: "50px",
                  border: "4px solid #f3f3f3",
                  borderTop: "4px solid #C20102",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              />
            </div>
          )}

          {/* Status Text */}
          <h3
            style={{
              color: textDark,
              marginBottom: "10px",
              fontSize: "20px",
              fontWeight: "600",
            }}
          >
            {isFailed && "Koneksi Gagal"}
            {!isFailed && socketStatus === "connecting" && "Menghubungkan ke Server..."}
            {!isFailed && socketStatus === "reconnecting" && "Mencoba Menghubungkan Kembali..."}
            {!isFailed && socketStatus === "disconnected" && "Koneksi Terputus"}
          </h3>

          <p
            style={{
              color: "#666",
              fontSize: "14px",
              lineHeight: "1.6",
              marginBottom: "20px",
            }}
          >
            {isFailed &&
              `Koneksi ke server telah terputus setelah ${MAX_RECONNECT_ATTEMPTS} kali percobaan. Silakan refresh halaman untuk mencoba kembali.`}
            {!isFailed && socketStatus === "connecting" && "Mohon tunggu, kami sedang menghubungkan Anda ke lelang."}
            {!isFailed &&
              socketStatus === "reconnecting" &&
              `Mencoba menghubungkan kembali... (${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`}
            {!isFailed &&
              socketStatus === "disconnected" &&
              "Koneksi ke server terputus. Mencoba menghubungkan kembali..."}
          </p>

          {/* Progress Bar for Reconnecting - Only show when reconnecting */}
          {!isFailed && socketStatus === "reconnecting" && (
            <div
              style={{
                width: "100%",
                height: "4px",
                background: "#f3f3f3",
                borderRadius: "2px",
                overflow: "hidden",
                marginTop: "15px",
              }}
            >
              <div
                style={{
                  width: `${(reconnectAttempts / MAX_RECONNECT_ATTEMPTS) * 100}%`,
                  height: "100%",
                  background: reconnectAttempts >= MAX_RECONNECT_ATTEMPTS * 0.7 ? "#ff9800" : "#C20102",
                  transition: "width 0.3s ease",
                }}
              />
            </div>
          )}

          {/* Refresh Button - Only show when failed */}
          {isFailed && (
            <Button
              variant="primary"
              onClick={handleRefreshPage}
              style={{
                padding: "10px 30px",
                fontSize: "16px",
                fontWeight: "500",
                marginTop: "10px",
                width: "100%",
              }}
            >
              Refresh Halaman
            </Button>
          )}

          {/* Helpful Tips - Only show when not failed */}
          {!isFailed && (
            <div
              style={{
                marginTop: "30px",
                padding: "15px",
                background: "#f8f9fa",
                borderRadius: "8px",
                fontSize: "12px",
                color: "#666",
                textAlign: "left",
              }}
            >
              <strong style={{ display: "block", marginBottom: "8px", color: textDark }}>💡 Tips:</strong>
              <ul style={{ margin: 0, paddingLeft: "20px", lineHeight: "1.8" }}>
                <li>Pastikan koneksi internet Anda stabil</li>
                <li>Jangan refresh halaman saat proses koneksi</li>
                <li>Jika gagal, halaman akan otomatis mencoba kembali</li>
              </ul>
            </div>
          )}

          {/* Error Details - Only show when failed */}
          {isFailed && (
            <div
              style={{
                marginTop: "20px",
                padding: "15px",
                background: "#fff3cd",
                borderRadius: "8px",
                fontSize: "12px",
                color: "#856404",
                textAlign: "left",
                border: "1px solid #ffeaa7",
              }}
            >
              <strong style={{ display: "block", marginBottom: "8px" }}>⚠️ Kemungkinan Penyebab:</strong>
              <ul style={{ margin: 0, paddingLeft: "20px", lineHeight: "1.8" }}>
                <li>Koneksi internet tidak stabil</li>
                <li>Server sedang maintenance</li>
                <li>Firewall atau antivirus memblokir koneksi</li>
              </ul>
            </div>
          )}

          {/* CSS Animations */}
          <style>
            {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            @keyframes pulse {
              0%, 100% { opacity: 1; transform: scale(1); }
              50% { opacity: 0.8; transform: scale(0.95); }
            }
          `}
          </style>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Loading/Error Placeholder - Show when not connected or failed, but NOT when join error */}
      {!joinError &&
        (socketStatus === "connecting" ||
          socketStatus === "reconnecting" ||
          socketStatus === "disconnected" ||
          socketStatus === "failed" ||
          maxReconnectReached) && <LoadingPlaceholder />}

      {/* Socket Status Indicator - Removed as placeholder handles all states */}

      {/* Show error page first if join failed */}
      {liveAuction === "NOT_FOUND" || liveAuction === "JOIN_ERROR" || joinError ? (
        // Render appropriate error page based on status code
        errorStatusCode === 400 ? (
          <Error400 errorMessage={errorMessage} />
        ) : errorStatusCode === 500 ? (
          <Error500 errorMessage={errorMessage} />
        ) : (
          <PageNotFound />
        )
      ) : liveAuction === "STARTED" ? (
        <>
          <div className="container">
            <div className="d-flex py-3 h-100 flex-column gap-3" style={{ minHeight: "100vh" }}>
              <div className="box-lelang overflow-hidden ">
                <div className="d-flex px-3 align-items-center justify-content-between">
                  <div className="d-flex gap-4">
                    <div className="py-3">
                      <span className="lelang-title">{auction?.name || "-"}</span>
                    </div>
                    <div className="d-flex align-items-center gap-4">
                      <div className="lelang-time mb-0">
                        <Time />
                        <span>
                          {auction?.date_start && auction?.time_start
                            ? format(new Date(`${auction.date_start} ${auction.time_start}`), "HH:mm")
                            : "-"}{" "}
                          -{" "}
                          {auction?.date_start && auction?.time_finish
                            ? format(new Date(`${auction?.date_start} ${auction?.time_finish}`), "HH:mm")
                            : "-"}{" "}
                          WIB
                        </span>
                      </div>
                      <div className="lelang-location mb-0">
                        <Location />
                        <span>{auction?.location_name || "-"}</span>
                      </div>
                    </div>
                  </div>
                  <div className="h-100">
                    <img src={logo} alt="" style={{ height: "48px", width: "137px" }} />
                  </div>
                </div>
              </div>
              <div className="row flex-grow-1">
                <div className="col-md-7">
                  <div className="box-border p-3 h-100">
                    <div className="row">
                      <div className="col-6">
                        <div className="position-relative">
                          <CarouselLayout autoplay={images.length > 1} autoplaySpeed={5000} pauseOnHover={true} lazyLoad="ondemand">
                            {images.length > 0 ? (
                              images.map((image, i) => (
                                <div key={"img" + i} style={{ height: "222px" }}>
                                  <img
                                    className="with-background detail-unit-slider"
                                    src={image.image_url || image.image || defaultImage}
                                    alt={`Unit preview ${i + 1}`}
                                    loading="lazy"
                                    style={{
                                      width: "100%",
                                      height: "222px",
                                      objectFit: "cover",
                                    }}
                                    onError={(e) => {
                                      e.target.src = defaultImage;
                                    }}
                                  />
                                </div>
                              ))
                            ) : (
                              <div style={{ height: "222px" }}>
                                <div
                                  className="with-background detail-unit-slider"
                                  style={{
                                    background: defaultImage ? `url(${defaultImage})` : "",
                                    height: "222px",
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                  }}
                                ></div>
                              </div>
                            )}
                          </CarouselLayout>

                          <div className="grade-desktop d-none d-md-block  ">
                            {data?.unit_inspection?.grade && (
                              <p className="nomor-lot ">
                                Grade
                                <span>{data?.unit_inspection?.grade}</span>
                              </p>
                            )}
                          </div>

                          {/* desktop view */}
                          <div className="grade-unit-auction d-none d-md-absolute">
                            Grade
                            <br />
                            <span className="grade-auction">{data?.unit_inspection?.grade || "-"}</span>
                          </div>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="h-100">
                          <div>
                            <span className="nomor-lot mb-2" style={{ fontSize: "1.2rem" }}>
                              Lot{" "}
                              {auction?.auction_lane_name && data?.lot_number
                                ? auction?.auction_lane_name + data?.lot_number
                                : "-"}
                            </span>
                            <div style={{ fontSize: "24px", fontWeight: 400 }}>{data?.unit_name || ""}</div>
                          </div>
                          <div>
                            <span className="unit-title mt-3">Harga Dasar</span>
                            <div className="fw-bold" style={{ fontSize: "24px" }}>
                              Rp. {data?.base_price !== undefined ? data?.base_price?.toLocaleString() : "0"}
                            </div>
                          </div>
                          <div>
                            <h2 className="unit-name-auction">{data?.unit_name || ""}</h2>
                            <span className="unit-title mt-3 mb-2">Highlight</span>
                            <div className="unit-highlight">
                              {data?.tags?.length > 0
                                ? data?.tags.map((row, i) => <span key={"tag" + i}>{row.name}</span>)
                                : "-"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <span className="unit-title mb-2">Detail Kendaraan</span>
                      <div style={{ maxHeight: "230px", overflowY: "auto" }}>
                        <div className="d-flex flex-wrap" style={{ gap: "8px" }}>
                          {data?.stock_informations?.length > 0
                            ? data?.stock_informations
                                ?.filter((el) => el.value !== "")
                                .map(({ label, value }, idx) => (
                                  <div className="informasi-unit-auction" key={`information-${idx + 1}`}>
                                    <span className="title mb-0">{label}</span>
                                    <span className="value">{value || "-"}</span>
                                  </div>
                                ))
                            : "-"}
                          {data?.stock_documents?.length > 0
                            ? data?.stock_documents
                                ?.filter((el) => el.value !== "")
                                .map(({ label, value }, idx) => (
                                  <div className="informasi-unit-auction" key={`information-${idx + 1}`}>
                                    <span className="title mb-0">{label}</span>
                                    <span className="value">{value || "-"}</span>
                                  </div>
                                ))
                            : "-"}
                        </div>
                      </div>
                      <div style={{ height: "135px" }}>
                        <div className="d-flex w-100 h-100" style={{ marginTop: "8px", paddingRight: "1.25rem" }}>
                          <div
                            className="d-flex flex-column align-items-start w-100"
                            style={{
                              padding: "8px",
                              border: "1px solid #DCDDE0",
                              borderRadius: "2px",
                            }}
                          >
                            <span
                              className="title mb-0"
                              style={{
                                fontWeight: 400,
                                fontSize: "12px",
                                lineHeight: "18px",
                                color: "#1E1E1F",
                              }}
                            >
                              Catatan
                            </span>
                            <span className="value">-</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-5">
                  <div className="h-100">
                    <div className="bidder-list d-flex flex-column h-100 mt-0" style={{ marginTop: "20px" }}>
                      <div className="d-flex justify-content-between align-items-center p-3">
                        <div className="font-sm">
                          <span className="me-2" style={{ textAlign: "right" }}>
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M20 22H4V20C4 18.6739 4.52678 17.4021 5.46447 16.4645C6.40215 15.5268 7.67392 15 9 15H15C16.3261 15 17.5979 15.5268 18.5355 16.4645C19.4732 17.4021 20 18.6739 20 20V22ZM12 13C11.2121 13 10.4319 12.8448 9.7039 12.5433C8.97595 12.2417 8.31451 11.7998 7.75736 11.2426C7.20021 10.6855 6.75825 10.0241 6.45672 9.2961C6.15519 8.56815 6 7.78793 6 7C6 6.21207 6.15519 5.43185 6.45672 4.7039C6.75825 3.97595 7.20021 3.31451 7.75736 2.75736C8.31451 2.20021 8.97595 1.75825 9.7039 1.45672C10.4319 1.15519 11.2121 1 12 1C13.5913 1 15.1174 1.63214 16.2426 2.75736C17.3679 3.88258 18 5.4087 18 7C18 8.5913 17.3679 10.1174 16.2426 11.2426C15.1174 12.3679 13.5913 13 12 13Z"
                                fill="#646566"
                              />
                            </svg>
                            <span className="ms-2">{totalNipl || 0} Orang mengikuti bidding</span>
                          </span>
                        </div>
                        <span className="timer">
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M9.99935 18.3337C14.6017 18.3337 18.3327 14.6027 18.3327 10.0003C18.3327 5.39795 14.6017 1.66699 9.99935 1.66699C5.39698 1.66699 1.66602 5.39795 1.66602 10.0003C1.66602 14.6027 5.39698 18.3337 9.99935 18.3337Z"
                              stroke="#C20102"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M10 5V10L13.3333 11.6667"
                              stroke="#C20102"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          {secondsToTime(counter)}
                        </span>
                      </div>
                      <div className="header py-2">
                        <span className="title mb-0" style={{ textAlign: "left" }}>
                          Harga Penawaran Saat Ini
                        </span>
                      </div>
                      <div className="highest-bid py-3" style={{ fontSize: "2rem" }}>
                        <span className="bid-value">
                          {highestBid?.offer_price ? highestBid?.offer_price?.toLocaleString() : "-"}
                        </span>
                        <span className="bidder">{highestBid?.type || "Harga Dasar"}</span>
                      </div>
                      <div className="position-relative" style={{ maxHeight: "490px", overflowY: "auto" }}>
                        {biddingList.length > 0 ? (
                          biddingList?.map((bid, index) => (
                            <div className={`bidder-item py-2`} key={`bid-${index}`}>
                              <span className="bid-value">Rp {bid?.offer_price.toLocaleString()}</span>
                              <span className="bidder">{bid?.type}</span>
                            </div>
                          ))
                        ) : (highestBid?.type === "Harga Dasar" || highestBid?.email === "base_price") ? (
                          <div className="bidder-item text-center font-sm text-secondary">
                            <span className="mx-auto">Belum ada yang penawaran</span>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {isOpen || isSelectingWinner ? (
            <ResultLelang status={isSelectingWinner ? "LOADING" : result} dataAuction={dataModal?.data_unit} />
          ) : null}
        </>
      ) : liveAuction === "PENDING" || liveAuction === "COMING" || liveAuction === "CLOSED" ? (
        <ArtworkPending status={liveAuction} />
      ) : null}
    </>
  );
};

export default LiveAuctionPublic;
